import { ClickAction, ObservationScene, Scene, SceneElement } from '../types';

function getDialogueFromAction(action: ClickAction | undefined): string {
  if (!action) return '';
  if (action.type === 'dialogue' && typeof action.payload === 'string') return action.payload;
  return getDialogueFromAction(action.nextAction);
}

/** Un objet inspectable = modale Signaler / Ne rien faire. */
export function isInspectObjectElement(element: SceneElement): boolean {
  if (element.type !== 'object') return false;
  if (element.inspectable === true) return true;
  const hasLegacyActions = Boolean(element.clickAction) && Boolean(element.dismissAction);
  const hasMessages = Boolean(element.reportMessage?.trim()) || Boolean(element.dismissMessage?.trim());
  return hasLegacyActions || hasMessages;
}

/**
 * Vrai si la scène doit se comporter comme une scène d'observation :
 * soit un bloc `observation` explicite, soit un bloc dont les éléments
 * portent des objets inspectables.
 */
export function isObjectInspectScene(scene: (Scene | { elements?: SceneElement[]; type?: string }) | null | undefined): boolean {
  if (!scene) return false;
  if (scene.type === 'observation') return true;
  return (scene.elements ?? []).some(isInspectObjectElement);
}

export function getInspectedFlag(elementId: string): string {
  return `inspected_${elementId}`;
}

export function getReportedFlag(elementId: string): string {
  return `reported_${elementId}`;
}

export function getObservationGateFlag(scene: Scene): string {
  return `${scene.id}_gate`;
}

/** Éléments object avec pedaShields > 0 (anomalies à signaler). */
export function getAnomalyElements(scene: Scene | null | undefined): SceneElement[] {
  if (!scene) return [];
  return (scene.elements ?? []).filter(
    (element) => element.type === 'object' && (element.pedaShields ?? 0) > 0
  );
}

export function resolveElementMessages(element: SceneElement): {
  reportMessage: string;
  dismissMessage: string;
} {
  if (element.reportMessage?.trim() || element.dismissMessage?.trim()) {
    return {
      reportMessage: element.reportMessage?.trim() ?? '',
      dismissMessage: element.dismissMessage?.trim() ?? '',
    };
  }

  const isAnomaly = (element.pedaShields ?? 0) > 0;
  if (isAnomaly && element.clickAction) {
    return {
      reportMessage: getDialogueFromAction(element.clickAction.nextAction),
      dismissMessage: getDialogueFromAction(element.dismissAction),
    };
  }

  return {
    reportMessage: getDialogueFromAction(element.clickAction),
    dismissMessage: getDialogueFromAction(element.dismissAction),
  };
}

export type InspectMarkerKind = 'shield' | 'warning' | 'success' | 'error';

/** Statut justesse × nature (spec observation-dev). */
export type InspectVerdictStatus = 'vp' | 'fn' | 'fp' | 'vn';

export const INSPECT_VERDICT_LABELS: Record<InspectVerdictStatus, string> = {
  vp: 'vu et arrêté',
  fn: 'danger manqué',
  fp: 'signalé pour rien',
  vn: 'à raison laissé',
};

/** Statut verdict pour l'UI observation (or = juste, rose = erreur). */
export function resolveInspectVerdictStatus(
  element: SceneElement,
  inspected: boolean,
  reported: boolean,
): InspectVerdictStatus | null {
  if (!inspected) return null;

  const isAnomaly = (element.pedaShields ?? 0) > 0;
  if (isAnomaly) return reported ? 'vp' : 'fn';
  return reported ? 'fp' : 'vn';
}

/** Classes de repli du badge selon la proximité des bords (coords centre %). */
export function resolveInspectBorderClasses(position: {
  x: number;
  y: number;
  width: number;
  height: number;
}): string[] {
  const classes: string[] = [];
  const rightEdge = position.x + position.width / 2;
  const topEdge = position.y - position.height / 2;
  if (rightEdge > 90) classes.push('bLeft');
  if (topEdge < 8) classes.push('bBottom');
  return classes;
}

/** Icône à afficher sur un objet inspecté selon la réponse du joueur. */
export function resolveInspectMarkerKind(
  element: SceneElement,
  inspected: boolean,
  reported: boolean,
): InspectMarkerKind | null {
  if (!inspected) return null;

  const isAnomaly = (element.pedaShields ?? 0) > 0;
  if (isAnomaly) {
    return reported ? 'shield' : 'warning';
  }

  return reported ? 'error' : 'success';
}

export function allAnomaliesReported(
  scene: Scene | null | undefined,
  flags: Record<string, boolean> | undefined
): boolean {
  const anomalies = getAnomalyElements(scene);
  if (anomalies.length === 0) return false;
  return anomalies.every((element) => Boolean(flags?.[getReportedFlag(element.id)]));
}

/** Toutes les anomalies ont été examinées au moins une fois (Signaler ou Rien à signaler). */
export function allAnomaliesInspected(
  scene: Scene | null | undefined,
  flags: Record<string, boolean> | undefined
): boolean {
  const anomalies = getAnomalyElements(scene);
  if (anomalies.length === 0) return false;
  return anomalies.every((element) => Boolean(flags?.[getInspectedFlag(element.id)]));
}

/**
 * Remet à zéro l'état d'inspection d'une scène observation à l'entrée.
 * Retourne les nouveaux flags, ou null si rien à changer.
 */
export function resetObservationState(
  scene: Scene | null | undefined,
  flags: Record<string, boolean> | undefined
): Record<string, boolean> | null {
  if (!scene || scene.type !== 'observation') return null;

  const gateFlag = getObservationGateFlag(scene);
  const elementIds = (scene.elements ?? []).map((element) => element.id);
  const keysToClear = [
    gateFlag,
    ...elementIds.flatMap((id) => [getInspectedFlag(id), getReportedFlag(id)]),
  ];

  const hasSomethingToClear = keysToClear.some((key) => flags?.[key]);
  if (!hasSomethingToClear) return null;

  const newFlags = { ...flags };
  for (const key of keysToClear) {
    newFlags[key] = false;
  }
  return newFlags;
}

/**
 * Réconcilie l'état d'inspection quand on arrive sur une scène d'observation legacy
 * (choice + clickAction) dont le puzzle a été réinitialisé mais la sauvegarde garde
 * des flags `inspected_` / `reported_` périmés.
 */
export function reconcileObservationFlags(
  scene: Scene | null | undefined,
  flags: Record<string, boolean> | undefined
): Record<string, boolean> | null {
  if (!scene || scene.type !== 'choice') return null;

  const inspectElements = (scene.elements ?? []).filter(isInspectObjectElement);
  if (inspectElements.length === 0) return null;

  const gateFlag = getObjectInspectGateFlag(scene);

  const hasStaleFlag = inspectElements.some(
    (element) => flags?.[getInspectedFlag(element.id)] || flags?.[getReportedFlag(element.id)]
  );
  if (!hasStaleFlag) return null;

  if (flags?.[gateFlag]) return null;

  const hasProgress = getAnomalyElements(scene).some(
    (element) => Boolean(flags?.[getReportedFlag(element.id)])
  ) || inspectElements.some((element) => {
    const progressFlag = getProgressFlagFromClickAction(element.clickAction);
    return progressFlag ? Boolean(flags?.[progressFlag]) : false;
  });
  if (hasProgress) return null;

  const newFlags = { ...flags };
  for (const element of inspectElements) {
    newFlags[getInspectedFlag(element.id)] = false;
    newFlags[getReportedFlag(element.id)] = false;
  }
  return newFlags;
}

export function getProgressFlagFromClickAction(action: ClickAction | undefined): string | null {
  let current = action;
  while (current) {
    if (
      current.type === 'setFlag'
      && typeof current.payload === 'object'
      && current.payload !== null
    ) {
      const payload = current.payload as { name: string; value: boolean };
      if (payload.value) return payload.name;
    }
    current = current.nextAction;
  }
  return null;
}

function findGateFlagInClickAction(action: ClickAction | undefined): string | undefined {
  if (!action) return undefined;

  if (
    action.type === 'setFlag'
    && action.condition
    && typeof action.payload === 'object'
    && action.payload !== null
  ) {
    return (action.payload as { name: string }).name;
  }

  return findGateFlagInClickAction(action.nextAction);
}

/** Gate flag pour scènes legacy choice + setFlag. */
export function getObjectInspectGateFlag(scene: Scene): string {
  if (scene.type === 'observation') {
    return getObservationGateFlag(scene);
  }

  const choice = scene.type === 'choice' ? scene : null;
  const optionWithGate = choice?.options.find((option) => option.condition?.startsWith('flag:'));
  const gateFromChoice = optionWithGate?.condition?.match(/^flag:(.+)$/)?.[1];
  if (gateFromChoice) return gateFromChoice;

  const anomalyWithGate = (scene.elements ?? []).find(
    (element) => element.type === 'object' && (element.pedaShields ?? 0) > 0 && element.clickAction
  );
  const gateFromAction = findGateFlagInClickAction(anomalyWithGate?.clickAction);
  if (gateFromAction) return gateFromAction;

  return `${scene.id}_gate`;
}

/** Gate pour scènes observation : toutes les anomalies examinées. */
export function syncObservationGate(
  scene: Scene | null | undefined,
  flags: Record<string, boolean> | undefined,
  setFlag: (name: string, value: boolean) => void
): void {
  if (!scene || scene.type !== 'observation') return;

  const gateFlag = getObservationGateFlag(scene);
  const gateOpen = allAnomaliesInspected(scene, flags);
  setFlag(gateFlag, gateOpen);
}

/** Gate legacy : tous les objets inspectables examinés au moins une fois. */
export function syncObjectInspectGate(
  scene: Scene | null | undefined,
  evaluateCondition: (condition: string) => boolean,
  setFlag: (name: string, value: boolean) => void,
  gateFlag?: string
): void {
  if (!scene || scene.type !== 'choice') return;

  const resolvedGateFlag = gateFlag ?? getObjectInspectGateFlag(scene);

  const inspectableObjects = (scene.elements ?? []).filter(isInspectObjectElement);

  const allExamined = inspectableObjects.length > 0
    && inspectableObjects.every((element) =>
      evaluateCondition(`flag:${getInspectedFlag(element.id)}`)
    );

  setFlag(resolvedGateFlag, allExamined);
}

export function getObservationCompletionDialogues(scene: Scene | null | undefined): string[] {
  if (!scene || scene.type !== 'observation') return [];
  return (scene as ObservationScene).completionDialogues ?? [];
}
